// User detail (platform admin) — full information on one signed-up user, as stacked cards:
// Identity, Companies (the original paginated/searchable list, kept as its own card), Practice
// (only when the user belongs to one), Subscriptions, Recent bugs, and Support access (active
// impersonation grants + history + the code-redeem control).
//
// Identity/Practice/Subscriptions/Recent-bugs/Support-access all come from ONE bundle
// (GET .../detail); Companies keeps its OWN existing paginated endpoint. The two loads are fired
// together and never gate each other (Promise.allSettled semantics) — a failure on either GET
// renders that side's own muted error and leaves the other card exactly as it would be if the
// call had succeeded. See the build report for why the split sits at exactly these two calls
// rather than one-request-per-card: the API bundles the first five into a single response.
import { html, signal, navigate } from '../shared/t4.js';
import { icon } from '../shared/icons.js';
import { api } from '../shared/api.js';
import { shell } from './shell.js';
import { pager, perPageSelect, DEFAULT_PER_PAGE } from '../shared/pager.js';
import { startImpersonation } from '../shared/impersonate.js';

export function userDetailScreen(id){
  // ── detail bundle: Identity / Practice / Subscriptions / Recent bugs / Support access ─────────
  const detail = signal(null), detailLoading = signal(true), detailErr = signal('');
  const impCode = signal(''), impBusy = signal(false), impErr = signal('');

  async function loadDetail(){
    detailLoading.value = true; detailErr.value = '';
    try { detail.value = await api.get('/admin/users/' + id + '/detail'); }
    catch(e){ detailErr.value = e.status === 404 ? 'User not found' : ((e.data && e.data.error) || e.message || 'Could not load this user'); }
    detailLoading.value = false;
  }

  async function doImpersonate(e){
    e.preventDefault();
    const code = impCode.value.trim();
    if(!code){ impErr.value = 'Enter the code the user gave you.'; return; }
    impBusy.value = true; impErr.value = '';
    try {
      const res = await api.post('/admin/users/' + id + '/impersonate', { code });
      startImpersonation(res, res.user);   // navigates to /home — this screen unmounts
    } catch(err){
      impErr.value = err.status === 403
        ? 'Invalid or expired code — ask the user to generate a new one in their Settings'
        : (err.data && err.data.error) || err.message || 'Could not start impersonation';
    }
    impBusy.value = false;
  }

  // ── companies: the original paginated/searchable list, unchanged endpoint + shape ─────────────
  const rows = signal([]), total = signal(0), page = signal(1), pages = signal(1),
        perPage = signal(DEFAULT_PER_PAGE), coLoading = signal(true), coErr = signal('');
  let coTimer, lastQ = '';

  async function loadCompanies(){
    coLoading.value = true;
    const params = new URLSearchParams({ per_page: perPage.value, page: String(page.value) });
    if(lastQ && lastQ.trim()) params.set('q', lastQ.trim());
    try {
      const r = await api.get('/admin/users/' + id + '/companies?' + params.toString());
      rows.value = r.data || []; total.value = r.total || 0; pages.value = r.total_pages || 1;
      coErr.value = '';
    } catch(e){
      rows.value = []; total.value = 0; pages.value = 1;
      coErr.value = e.status === 404 ? 'User not found' : 'Could not load companies';
    }
    coLoading.value = false;
  }
  function onSearchCo(e){ lastQ = e.target.value; page.value = 1; clearTimeout(coTimer); coTimer = setTimeout(loadCompanies, 220); }
  function onPerCo(e){ perPage.value = e.target.value; page.value = 1; loadCompanies(); }
  function goPageCo(p){ page.value = p; loadCompanies(); }

  // Two independent GETs — allSettled semantics: a rejection on either must never blank the
  // other's card. Each function already owns its own try/catch → its own signals, so simply
  // firing both makes that true; wrapped in allSettled to say so explicitly for anyone reading.
  Promise.allSettled([loadDetail(), loadCompanies()]);

  // one-line "role · country · status · created …" descriptor (drops any missing part)
  const subLine = c => [c.role, c.country, c.status, c.created ? 'created ' + c.created : null]
    .filter(Boolean).join(' · ');

  // ── card renderers — plain functions called directly from the ONE reactive block that owns
  // their signals (never nested ${() => …} — see CLAUDE.md's append gotcha) ─────────────────────
  function identityCard(u){
    return html`<div class="rail-card">
      <div class="rail-card-title">Identity</div>
      <div class="detail-grid">
        <div class="field"><div class="field-label">Email</div><div class="field-val">${u.email || '—'}</div></div>
        <div class="field"><div class="field-label">Display name</div><div class="field-val">${u.display_name || '—'}</div></div>
        <div class="field"><div class="field-label">Kind</div><div class="field-val">${u.signup_kind || '—'}</div></div>
        <div class="field"><div class="field-label">Status</div><div class="field-val">${u.is_active ? html`<span class="badge badge-primary">Active</span>` : html`<span class="tag-off">Inactive</span>`}</div></div>
        <div class="field"><div class="field-label">Created</div><div class="field-val">${u.created_at || '—'}</div></div>
      </div>
      <div class="field mt-2"><div class="field-label">Keycloak subject</div><div class="field-val mono id-sub">${u.keycloak_sub || '—'}</div></div>
    </div>`;
  }

  function practiceCard(p){
    if(!p) return '';
    return html`<div class="rail-card">
      <div class="rail-card-title">Practice</div>
      <div class="detail-grid">
        <div class="field"><div class="field-label">Name</div><div class="field-val">${p.name || '—'}</div></div>
        <div class="field"><div class="field-label">Role</div><div class="field-val">${p.role || '—'}</div></div>
      </div>
    </div>`;
  }

  function subscriptionsCard(subs){
    return html`<div class="rail-card">
      <div class="rail-card-title">Subscriptions</div>
      ${!subs.length ? html`<div class="muted-block">No subscriptions.</div>` : html`
      <div class="table-wrap"><table class="cb-table">
        <thead><tr><th>Company</th><th>Tier</th><th>Status</th><th>Period end</th></tr></thead>
        <tbody>${subs.map(s => html`<tr>
          <td>${s.company_name}</td>
          <td><span class="badge badge-light">${s.tier_name}</span></td>
          <td>${s.status}</td>
          <td class="mono">${s.current_period_end || '—'}</td>
        </tr>`)}</tbody>
      </table></div>`}
    </div>`;
  }

  function bugsCard(bugs){
    return html`<div class="rail-card">
      <div class="rail-card-title">Recent bugs</div>
      ${!bugs.length ? html`<div class="muted-block">No bugs reported.</div>` : html`
      <div class="row-list">${bugs.map(b => html`
        <div class="row-card" @click=${() => navigate('/bugs')}>
          <span class="row-main"><b>${b.title}</b><span class="row-sub">${b.created_at || ''}</span></span>
          <span class="badge badge-light">${b.status || 'open'}</span>
          <span class="row-chevron">${icon('chevron-right', {size: 14})}</span>
        </div>`)}</div>`}
    </div>`;
  }

  function supportCard(imp, busy, err){
    const active = (imp && imp.active) || [];
    const history = ((imp && imp.history) || []).slice(0, 10);
    return html`<div class="rail-card">
      <div class="rail-card-title">Support access</div>
      ${!active.length ? html`<div class="muted-block">No active support session.</div>` : html`
      <div class="imp-active-list">${active.map(a => html`
        <div class="imp-active-row">
          <span class="badge badge-primary">Active</span>
          <span>${a.admin}</span>
          <span class="muted">started ${a.created_at || '—'} · ends ${a.expires_at || '—'}</span>
        </div>`)}</div>`}

      <form class="imp-control-row" @submit=${doImpersonate}>
        <input class="form-control" .value=${impCode}
               @input=${e => { impCode.value = e.target.value; if(impErr.value) impErr.value = ''; }}
               placeholder="e.g. K7RD2MWQ">
        <button type="submit" class="btn btn-primary" ?disabled=${busy}>${busy ? 'Starting…' : 'Impersonate'}</button>
      </form>
      ${err ? html`<div class="alert alert-danger py-2 mb-2">${err}</div>` : ''}

      <div class="rail-card-title mt-2">History</div>
      ${!history.length ? html`<div class="muted-block">No previous support sessions.</div>` : html`
      <div class="table-wrap"><table class="cb-table">
        <thead><tr><th>Admin</th><th>Started</th><th>Ended</th></tr></thead>
        <tbody>${history.map(h => html`<tr>
          <td>${h.admin}</td><td class="mono">${h.created_at || '—'}</td><td class="mono">${h.ended_at || '—'}</td>
        </tr>`)}</tbody>
      </table></div>`}
    </div>`;
  }

  // ── the detail-bundle stack: ONE reactive block, every signal it needs read at the top ────────
  const detailPanels = () => {
    if(detailLoading.value) return html`<div class="rail-card"><div class="muted-block">Loading…</div></div>`;
    if(detailErr.value) return html`<div class="rail-card"><div class="muted-block">${detailErr.value}</div></div>`;
    const d = detail.value;
    if(!d) return html`<div class="rail-card"><div class="muted-block">Loading…</div></div>`;
    const busy = impBusy.value, err = impErr.value;
    return html`<div class="set-stack">
      ${identityCard(d.user || {})}
      ${practiceCard(d.practice)}
      ${subscriptionsCard(d.subscriptions || [])}
      ${bugsCard(d.bugs || [])}
      ${supportCard(d.impersonation || {}, busy, err)}
    </div>`;
  };

  // ── companies card: its OWN reactive block (independent of the bundle above) ───────────────────
  const companiesPanel = () => {
    const total_ = total.value;
    let body;
    if(coLoading.value) body = html`<div class="muted-block">Loading…</div>`;
    else if(coErr.value) body = html`<div class="muted-block">${coErr.value}</div>`;
    else if(!rows.value.length) body = html`<div class="muted-block">This user can't reach any companies yet.</div>`;
    else {
      const pg = () => pager({ page: page.value, totalPages: pages.value, total: total.value,
                               perPage: Number(perPage.value), onPage: goPageCo });
      body = html`<div class="list-wrap">
        ${pg()}
        <div class="row-list">${rows.value.map(c => html`
          <div class="row-card">
            <span class="row-main"><b>${c.name}</b><span class="row-sub">${subLine(c)}</span></span>
            ${c.is_demo ? html`<span class="demo-badge">Demo</span>` : ''}
          </div>`)}</div>
        ${pg()}
      </div>`;
    }
    return html`<div class="rail-card">
      <div class="rail-card-title">${'Companies (' + total_ + ')'}</div>
      <div class="toolbar">
        <input class="form-control" type="search" placeholder="Search companies… e.g. Riverside" @input=${onSearchCo}>
        ${perPageSelect(perPage.value, onPerCo)}
      </div>
      ${body}
    </div>`;
  };

  const content = html`
    <div class="page">
      <div class="page-head">
        <a class="back-link" @click=${() => navigate('/users')}>${icon('chevron-left', {size: 14})} Users</a>
        <h1 class="page-title">${() => {
          const d = detail.value;
          return (d && d.user && (d.user.display_name || d.user.email)) || 'User';
        }}</h1>
      </div>
      <div class="set-stack">
        ${detailPanels}
        ${companiesPanel}
      </div>
    </div>`;

  return shell('users', 'Users', content);
}
